import * as React from "react";
import { useEffectOnce } from "utils";

type ChildProps = {
  type?: string;
  name?: string;
  label?: string;
  subsectionId?: string;
  subsectionLabel?: string;
  header?: React.ReactNode;
  children?: React.ReactNode;
  content?: {
    props: {
      children?: React.ReactNode;
    };
  };
};

function useListChartElements(
  parentChildren: React.ReactNode,
  setState: ((chartElementList: Record<string, string>[]) => void) | undefined,
  setStringList?: ((chartElementList: string[]) => void) | undefined
) {
  useEffectOnce(() => {
    const filteredElements: React.ReactElement["props"][] = [];

    const recursivelySearchChildren = (children: React.ReactNode) => {
      React.Children.forEach(children, (child) => {
        if (React.isValidElement(child)) {
          const typedChild = child as React.ReactElement<ChildProps>;

          // Subsection headers: include them as structural elements using subsectionId/label
          if (typedChild.props.subsectionId) {
            filteredElements.push({
              name: typedChild.props.subsectionId,
              label: typedChild.props.subsectionLabel,
              type: "header",
            });
          }

          if (typedChild.props.name) {
            filteredElements.push(typedChild.props);
          }

          if (typedChild.props.header) {
            recursivelySearchChildren(typedChild.props.header);
          }

          if (typedChild.props.content?.props?.children) {
            recursivelySearchChildren(typedChild.props.content?.props.children);
          }

          if (typedChild.props.children) {
            recursivelySearchChildren(typedChild.props.children);
          }
        }
      });
    };

    recursivelySearchChildren(parentChildren);

    const stringList = filteredElements.map((el) => {
      const name = el.name || "";
      const label = el.label || "";
      const type = el.type || "";
      return `${name}:${label}:${type}`;
    });

    setStringList?.(stringList);

    setState?.(filteredElements);
  });
}

export { useListChartElements };
